import React, { useState, useEffect } from 'react';
import { formatDate, formatDateForInput, todayStr, firstDayOfMonthStr } from '../utils/dateUtils';
import MaskedDatePicker from './MaskedDatePicker';
import {
  Package,
  User,
  DollarSign,
  ShoppingCart,
  Calendar,
  MapPin,
  FileText,
  TrendingUp,
  ChevronUp,
  ChevronDown,
  Download
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { createProfileImageUrl } from '../utils/imageProxy';
import { salesTeamService } from '../services/salesTeamService';

const ProductDetailsBySalesTeamComponent = () => {
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [productCode, setProductCode] = useState('');
  const [groupedData, setGroupedData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [salesTeamNames, setSalesTeamNames] = useState({});
  const [loadingNames, setLoadingNames] = useState(false);
  const [teamsData, setTeamsData] = useState([]);
  const [salesTeamMap, setSalesTeamMap] = useState(new Map());
  const [imageErrors, setImageErrors] = useState({});
  const [factoryProducts, setFactoryProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'net_amount', direction: 'desc' });
  const [productCodeSearch, setProductCodeSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedProductInfo, setSelectedProductInfo] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    setDateFrom(firstDayOfMonthStr());
    setDateTo(todayStr());
    
    // Fetch factory products when component mounts
    fetchFactoryProducts();
    // Fetch sales teams data
    fetchSalesTeamsData();
  }, []);

  const fetchFactoryProducts = async () => {
    setLoadingProducts(true);
    
    try {
      const response = await fetch('https://apisaleeasy.psisat.com/api/factory', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.status && result.data && result.data.length > 0) {
        setFactoryProducts(result.data);
        if (result.data[0].num_name_products) {
          setProductCode(result.data[0].num_name_products);
          setProductCodeSearch(result.data[0].num_name_products);
          setSelectedProductInfo(result.data[0]);
        }
      } else {
        console.warn('No factory products found');
      }
    } catch (err) {
      console.error('Error fetching factory products:', err);
      const fallbackProducts = [
        { num_name_products: '885010020', factory_name_products: 'รายการ 1' },
        { num_name_products: '885010013', factory_name_products: 'รายการ 2' },
        { num_name_products: '885030001', factory_name_products: 'รายการ 3' },
        { num_name_products: 'B01', factory_name_products: 'รายการ 4' }
      ];
      setFactoryProducts(fallbackProducts);
      setProductCode('885010020');
      setProductCodeSearch('885010020');
      setSelectedProductInfo(fallbackProducts[0]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchSalesTeamsData = async () => {
    try {
      const response = await salesTeamService.getTeamsWithMembers();
      
      if (response.success) {
        setTeamsData(response.data);
        
        // Create a map for quick lookup: salesman_code -> member info
        const teamMap = new Map();
        response.data.forEach(team => {
          if (team.members && Array.isArray(team.members)) {
            team.members.forEach(member => {
              teamMap.set(member.salesman_code, {
                ...member,
                team_name: team.team_name
              });
            });
          }
        });
        
        setSalesTeamMap(teamMap);
      } else {
        console.error('Failed to fetch sales teams:', response.error);
      }
    } catch (error) {
      console.error('Error fetching sales teams:', error);
    }
  };

  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const sortData = (data) => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      
      if (sortConfig.direction === 'asc') {
        if (typeof aVal === 'string') {
          return aVal.localeCompare(bVal);
        }
        return aVal - bVal;
      } else {
        if (typeof aVal === 'string') {
          return bVal.localeCompare(aVal);
        }
        return bVal - aVal;
      }
    });
  };

  const getSortIcon = (column) => {
    if (sortConfig.key !== column) {
      return <ChevronUp className="w-4 h-4 text-gray-400" />;
    }
    return sortConfig.direction === 'desc' ? 
      <ChevronDown className="w-4 h-4 text-gray-600" /> : 
      <ChevronUp className="w-4 h-4 text-gray-600" />;
  };

  // Filter products based on search - show default 50 if search is empty
  const filteredProducts = factoryProducts.filter(product => {
    if (!product.num_name_products) return false;
    
    // If search is empty, show first 50 products (will be sliced later)
    if (!productCodeSearch.trim()) return true;
    
    // Otherwise filter by search term
    const searchTerm = productCodeSearch.toLowerCase();
    const productCode = product.num_name_products.toLowerCase();
    const productName = (product.factory_name_products || '').toLowerCase();
    
    return productCode.includes(searchTerm) || productName.includes(searchTerm);
  });

  // Get display products - limit to 50 if no search, or 10 if searching
  const displayProducts = productCodeSearch.trim() ? 
    filteredProducts.slice(0, 10) : 
    filteredProducts.slice(0, 50);

  // Handle product selection from dropdown
  const handleProductSelect = (productCode) => {
    const selectedProduct = factoryProducts.find(p => p.num_name_products === productCode);
    setProductCode(productCode);
    setProductCodeSearch(productCode);
    setSelectedProductInfo(selectedProduct);
    setShowProductDropdown(false);
  };

  // Handle search input
  const handleProductSearchChange = (e) => {
    const value = e.target.value;
    setProductCodeSearch(value);
    setProductCode(value);
    setShowProductDropdown(true);
  };

  // Get sales team info from the map, with fallback to API call
  const getSalesTeamInfo = (salesmanCode) => {
    const teamInfo = salesTeamMap.get(salesmanCode);
    if (teamInfo) {
      return teamInfo;
    }
    
    // If not found in teamMap, try to get from salesTeamNames (fallback)
    const salesmanName = salesTeamNames[salesmanCode];
    if (salesmanName && salesmanName !== salesmanCode) {
      return {
        salesman_code: salesmanCode,
        salesman_name: salesmanName,
        team_name: null,
        image_path: null
      };
    }
    
    return null;
  };

  // Fetch individual sales team member info using the new API
  const fetchIndividualSalesTeamInfo = async (salesmanCode) => {
    if (salesTeamNames[salesmanCode] || salesTeamMap.has(salesmanCode)) return; // Already loaded
    
    try {
      const response = await fetch('https://apisaleeasy.psisat.com/api/sales-team/member-by-salecode', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          salemancode: salesmanCode
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.status && result.data && result.data.member_info) {
          const memberInfo = result.data.member_info;
          const fullName = `${memberInfo.sale_name} ${memberInfo.sale_lastname}`;
          
          // Update both maps
          setSalesTeamNames(prev => ({ 
            ...prev, 
            [salesmanCode]: fullName 
          }));
          
          // Add to salesTeamMap for complete info including image
          setSalesTeamMap(prev => {
            const newMap = new Map(prev);
            
            newMap.set(salesmanCode, {
              salesman_code: memberInfo.sale_salemancode,
              salesman_name: fullName,
              image_path: memberInfo.sale_picture,
              team_name: memberInfo.team_name || null,
              member_id: memberInfo.member_id
            });
            return newMap;
          });
        }
      }
    } catch (error) {
      console.error(`Error fetching individual sales team info for ${salesmanCode}:`, error);
    }
  };

  // Handle image error
  // Convert image path using SSL-safe proxy (matching SalesTeamComponent)
  const convertImagePath = (imagePath) => {
    return createProfileImageUrl(imagePath);
  };

  const handleImageError = (salesmanCode) => {
    setImageErrors(prev => ({
      ...prev,
      [salesmanCode]: true
    }));
  };

  const fetchProductSalesHistory = async () => {
    if (!dateFrom || !dateTo || !productCode) {
      setError('กรุณาเลือกวันที่และรหัสสินค้า');
      return;
    }

    setLoading(true);
    setError('');
    setHasSearched(true);
    
    try {
      const response = await fetch(
        `https://apisaleeasy.psisat.com/api/analytics/product-sales/purchase-history?productCode=${productCode}&dateFrom=${dateFrom}&dateTo=${dateTo}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success || result.data) {
        const data = result.data || [];
        setSalesData(data);
        setTotalRecords(result.total_records || data.length);
        
        // Group data by salesman_code instead of customer_code
        const grouped = groupBySalesTeam(data);
        setGroupedData(grouped);
        
        // Fetch individual sales team info for missing members
        const missingMembers = grouped
          .map(item => item.salesman_code)
          .filter(code => !salesTeamMap.has(code) && !salesTeamNames[code]);
          
        if (missingMembers.length > 0) {
          missingMembers.forEach(code => fetchIndividualSalesTeamInfo(code));
        }
      } else {
        throw new Error(result.message || 'เกิดข้อผิดพลาดในการดึงข้อมูล');
      }
    } catch (err) {
      console.error('Error fetching product sales history:', err);
      setError('เกิดข้อผิดพลาดในการดึงข้อมูล: ' + err.message);
      setSalesData([]);
      setGroupedData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  // Group by sales team instead of customer
  const groupBySalesTeam = (data) => {
    const grouped = {};
    
    data.forEach(item => {
      const salesmanCode = item.salesman_code || 'N/A';
      
      if (!grouped[salesmanCode]) {
        grouped[salesmanCode] = {
          salesman_code: salesmanCode,
          salesman_name: item.salesman_name || salesmanCode,
          total_quantity: 0,
          total_amount: 0,
          net_amount: 0,
          invoice_count: 0,
          customer_count: 0,
          customers: new Set(),
          invoices: []
        };
      }
      
      grouped[salesmanCode].total_quantity += parseInt(item.quantity) || 0;
      grouped[salesmanCode].total_amount += parseFloat(item.line_amount) || 0;
      grouped[salesmanCode].net_amount += parseFloat(item.net_line_amount) || 0;
      grouped[salesmanCode].invoice_count += 1;
      grouped[salesmanCode].customers.add(item.customer_code);
      grouped[salesmanCode].invoices.push(item);
    });
    
    // Convert Set to count and array for final result
    Object.values(grouped).forEach(group => {
      group.customer_count = group.customers.size;
      group.customers = Array.from(group.customers);
    });
    
    return Object.values(grouped);
  };

  const formatNumber = (num) => {
    if (typeof num !== 'number') return '0';
    return num.toLocaleString('th-TH');
  };

  const formatCurrency = (num) => {
    if (typeof num !== 'number') return '฿0.00';
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 2
    }).format(num);
  };

  const sortedData = sortData(groupedData);

  // Export to Excel function
  const exportToExcel = () => {
    if (!groupedData || groupedData.length === 0) {
      alert('ไม่มีข้อมูลที่จะ export');
      return;
    }

    // Prepare header info
    const productInfo = selectedProductInfo ? 
      `${selectedProductInfo.num_name_products}${selectedProductInfo.factory_name_products ? ' - ' + selectedProductInfo.factory_name_products : ''}` : 
      'ไม่ระบุ';
    
    const dateRange = `${dateFrom} ถึง ${dateTo}`;
    
    // Prepare data for export
    const exportData = sortedData.map((item, index) => {
      const teamInfo = getSalesTeamInfo(item.salesman_code);
      return {
        'ลำดับ': index + 1,
        'รหัสพนักงานขาย': item.salesman_code,
        'ชื่อพนักงานขาย': teamInfo?.salesman_name || item.salesman_name || item.salesman_code,
        'ทีมขาย': teamInfo?.team_name || '-',
        'จำนวนลูกค้า': item.customer_count,
        'ยอดขาย': item.total_quantity,
        'มูลค่า (บาท)': item.net_amount.toFixed(2),
        'จำนวนบิล': item.invoice_count
      };
    });

    // Create workbook
    const wb = XLSX.utils.book_new();
    
    // Create header rows
    const headerData = [
      ['รายงานสินค้ารายตัวทีมขาย'],
      ['สินค้า: ' + productInfo],
      ['ช่วงวันที่: ' + dateRange],
      ['วันที่ออกรายงาน: ' + formatDate(new Date())],
      [], // Empty row
      ['ลำดับ', 'รหัสพนักงานขาย', 'ชื่อพนักงานขาย', 'ทีมขาย', 'จำนวนลูกค้า', 'ยอดขาย', 'มูลค่า (บาท)', 'จำนวนบิล']
    ];
    
    // Add data rows
    exportData.forEach(row => {
      headerData.push([
        row['ลำดับ'],
        row['รหัสพนักงานขาย'],
        row['ชื่อพนักงานขาย'],
        row['ทีมขาย'],
        row['จำนวนลูกค้า'],
        row['ยอดขาย'],
        row['มูลค่า (บาท)'],
        row['จำนวนบิล']
      ]);
    });
    
    const ws = XLSX.utils.aoa_to_sheet(headerData);

    // Set column widths
    ws['!cols'] = [
      { wch: 8 },  // ลำดับ
      { wch: 15 }, // รหัสพนักงานขาย
      { wch: 25 }, // ชื่อพนักงานขาย
      { wch: 20 }, // ทีมขาย
      { wch: 12 }, // จำนวนลูกค้า
      { wch: 12 }, // ยอดขาย
      { wch: 15 }, // มูลค่า
      { wch: 12 }  // จำนวนบิล
    ];

    // Merge cells for header
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 7 } }, // รายงานสินค้ารายตัวทีมขาย
      { s: { r: 1, c: 0 }, e: { r: 1, c: 7 } }, // สินค้า
      { s: { r: 2, c: 0 }, e: { r: 2, c: 7 } }, // ช่วงวันที่
      { s: { r: 3, c: 0 }, e: { r: 3, c: 7 } }  // วันที่ออกรายงาน
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'สินค้ารายตัวทีมขาย');

    // Generate filename with current date
    const productName = selectedProductInfo?.num_name_products || 'Unknown';
    const filename = `สินค้ารายตัวทีมขาย_${productName}_${todayStr()}.xlsx`;

    // Save file
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    saveAs(blob, filename);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
            <Package className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">สินค้ารายตัวทีมขาย</h2>
            <p className="text-gray-600">ข้อมูลการขายสินค้าแยกตามพนักงานขาย</p>
          </div>
        </div>

        {/* Filter Section */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                รหัสสินค้า
              </label>
              <input
                type="text"
                value={productCodeSearch}
                onChange={handleProductSearchChange}
                onFocus={() => setShowProductDropdown(true)}
                onBlur={() => setTimeout(() => setShowProductDropdown(false), 200)}
                placeholder="ค้นหาหรือเลือกรหัสสินค้า"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                disabled={loadingProducts}
              />
              
              {/* Searchable Dropdown */}
              {showProductDropdown && displayProducts.length > 0 && (
                <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto mt-1">
                  {displayProducts.map((product) => (
                    <button
                      key={product.num_name_products}
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 border-b border-gray-100 last:border-b-0"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleProductSelect(product.num_name_products)}
                    >
                      <div className="font-medium text-gray-900">
                        {product.num_name_products}
                      </div>
                      {product.factory_name_products && (
                        <div className="text-sm text-gray-500 truncate">
                          {product.factory_name_products}
                        </div>
                      )}
                    </button>
                  ))}
                  {filteredProducts.length > displayProducts.length && (
                    <div className="px-3 py-2 text-sm text-gray-500 bg-gray-50">
                      {productCodeSearch.trim() ? 
                        `แสดง ${displayProducts.length} รายการแรก จาก ${filteredProducts.length} รายการที่พบ` :
                        `แสดง ${displayProducts.length} รายการแรก จาก ${filteredProducts.length} รายการทั้งหมด`
                      }
                    </div>
                  )}
                </div>
              )}
              
              {showProductDropdown && filteredProducts.length === 0 && productCodeSearch.trim() && (
                <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg mt-1">
                  <div className="px-3 py-2 text-sm text-gray-500">
                    ไม่พบรหัสสินค้า "{productCodeSearch}"
                  </div>
                </div>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                วันที่เริ่มต้น
              </label>
              <MaskedDatePicker
                value={dateFrom}
                onChange={setDateFrom}
                placeholderText="DD/MM/YYYY"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                วันที่สิ้นสุด
              </label>
              <MaskedDatePicker
                value={dateTo}
                onChange={setDateTo}
                placeholderText="DD/MM/YYYY"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            
            <div className="flex items-end">
              <button
                onClick={fetchProductSalesHistory}
                disabled={loading || !dateFrom || !dateTo || !productCode}
                className="w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    กำลังค้นหา...
                  </div>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    ค้นหาข้อมูล
                  </>
                )}
              </button>
            </div>

            <div className="flex items-end">
              <button
                onClick={exportToExcel}
                disabled={loading || groupedData.length === 0}
                className="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Excel
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        {groupedData.length > 0 && hasSearched && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center">
                <User className="w-8 h-8 text-blue-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-blue-600">จำนวนพนักงานขาย</p>
                  <p className="text-2xl font-bold text-blue-900">{groupedData.length}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <ShoppingCart className="w-8 h-8 text-green-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-green-600">ยอดขายรวม</p>
                  <p className="text-2xl font-bold text-green-900">
                    {formatNumber(groupedData.reduce((sum, item) => sum + item.total_quantity, 0))}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center">
                <DollarSign className="w-8 h-8 text-orange-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-orange-600">มูลค่ารวม</p>
                  <p className="text-2xl font-bold text-orange-900">
                    {formatCurrency(groupedData.reduce((sum, item) => sum + item.net_amount, 0))}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Results Table */}
        {groupedData.length > 0 && hasSearched && (
          <div className="overflow-x-auto">
            <div className="mb-4 flex justify-between items-start">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  ผลการค้นหา ({totalRecords} รายการ)
                </h3>
                {selectedProductInfo && (
                  <div className="mt-1 text-sm text-gray-600">
                    <span className="font-medium">สินค้า:</span> {selectedProductInfo.num_name_products}
                    {selectedProductInfo.factory_name_products && (
                      <span> - {selectedProductInfo.factory_name_products}</span>
                    )}
                  </div>
                )}
              </div>
              
              <div className="flex flex-col items-end text-sm">
                {salesTeamMap.size === 0 && (
                  <div className="text-orange-600 mb-1">
                    กำลังโหลดข้อมูลพนักงานขาย...
                  </div>
                )}
              </div>
            </div>
            
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    #
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('salesman_code')}
                  >
                    <div className="flex items-center">
                      พนักงานขาย
                      {getSortIcon('salesman_code')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('customer_count')}
                  >
                    <div className="flex items-center justify-end">
                      จำนวนลูกค้า
                      {getSortIcon('customer_count')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('total_quantity')}
                  >
                    <div className="flex items-center justify-end">
                      ยอดขาย
                      {getSortIcon('total_quantity')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('net_amount')}
                  >
                    <div className="flex items-center justify-end">
                      มูลค่า (บาท)
                      {getSortIcon('net_amount')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('invoice_count')}
                  >
                    <div className="flex items-center justify-end">
                      จำนวนบิล
                      {getSortIcon('invoice_count')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedData.map((item, index) => (
                  <tr key={item.salesman_code} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {(() => {
                          const teamInfo = getSalesTeamInfo(item.salesman_code);
                          return (
                            <>
                              {/* Profile Image */}
                              <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center mr-3 flex-shrink-0">
                                {convertImagePath(teamInfo?.image_path) && !imageErrors[item.salesman_code] ? (
                                  <img 
                                    src={convertImagePath(teamInfo.image_path)} 
                                    alt={teamInfo.salesman_name || item.salesman_code}
                                    className="w-full h-full object-cover"
                                    style={{ 
                                      imageRendering: 'crisp-edges',
                                      imageRendering: '-webkit-optimize-contrast'
                                    }}
                                    crossOrigin="anonymous"
                                    referrerPolicy="no-referrer"
                                    onError={() => handleImageError(item.salesman_code)}
                                  />
                                ) : (
                                  <User className="w-5 h-5 text-gray-400" />
                                )}
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {teamInfo?.salesman_name || item.salesman_name || item.salesman_code}
                                </div>
                                <div className="text-xs text-gray-500">
                                  รหัส: {item.salesman_code}
                                </div>
                                {teamInfo?.team_name && (
                                  <div className="text-xs text-gray-500">
                                    ทีม: {teamInfo.team_name}
                                  </div>
                                )}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {formatNumber(item.customer_count)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm font-medium text-gray-900">
                        {formatNumber(item.total_quantity)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm font-medium text-green-600">
                        {formatCurrency(item.net_amount)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {formatNumber(item.invoice_count)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* No Results Message */}
        {!loading && groupedData.length === 0 && !error && hasSearched && (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">ไม่พบข้อมูล</h3>
            <p className="text-gray-600">ไม่พบข้อมูลการขายสำหรับสินค้าและช่วงวันที่ที่เลือก</p>
          </div>
        )}

        {/* Initial State Message */}
        {!loading && !hasSearched && (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">เริ่มต้นใช้งาน</h3>
            <p className="text-gray-600">กรุณาเลือกรหัสสินค้า วันที่เริ่มต้น และวันที่สิ้นสุด แล้วกดปุ่มค้นหาข้อมูล</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetailsBySalesTeamComponent;